import { Button, Checkbox, Flex, LoadingOverlay, Modal, Select, Text, TextInput } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { useDebouncedValue } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { isValid, parseISO } from 'date-fns'
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz'
import { useEffect, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useLocation } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'

import { useAuth } from '@/auth/use-auth'
import DSPhoneInput from '@/components/DSPhoneInput'
import { useBranches } from '@/hooks/branch/useBranches'
import { useCreateCustomer } from '@/hooks/customer/useCreateCustomer'
import { useGetCustomerByEmail } from '@/hooks/customer/useGetCustomerByEmail'
import { useMarketingSources } from '@/hooks/referrer/useMarketingSources'
import { useMoveManagerStore } from '@/pages/moveManager/stores/useMoveManagerStore'
import { DEFAULT_TIMEZONE } from '@/types/constants'
import { parseLocalDate } from '@/utils/dateUtils'

import { emailRegex, phoneTypes, REPEAT_REFERRAL, resolveRepeatAfterEmailLookup } from './utils'

interface ModalNewCustomerProps {
  opened: boolean
  onClose: () => void
}

interface Inputs {
  firstName: string
  lastName: string
  email: string
  phone: string
  phoneType: string
  notes: string
  branchId: string
  altFirstName: string
  altLastName: string
  altPhone: string
  altPhoneType: string
  altEmail: string
  referralSource: string
  isRepeat: boolean
  requestedDay: Date
  referredBy: string
  previousContractId: string
}

const ModalNewCustomer = ({ opened, onClose }: ModalNewCustomerProps) => {
  const { branchId } = useAuth()
  const location = useLocation()

  const { date: moveManagerDate, currentMainBranch } = useMoveManagerStore(
    useShallow((state) => ({
      date: state.date,
      currentMainBranch: state.currentMainBranch,
    })),
  )

  const getDefaultRequestedDay = () => {
    if (location.pathname.includes('move-manager')) {
      if (typeof moveManagerDate === 'string') {
        const parsed = parseISO(moveManagerDate)
        return isValid(parsed) ? parsed : new Date()
      }
      return moveManagerDate instanceof Date ? moveManagerDate : new Date()
    }
    return new Date()
  }

  const {
    data: marketingSourcesData,
    isLoading: isLoadingMarketingSources,
    error: marketingSourcesError,
  } = useMarketingSources()

  const { data: branchesData, isLoading: isLoadingBranches, error: branchesError } = useBranches()

  const { mutateAsync: createCustomerAsync, isPending: isLoadingCreatingCustomer } = useCreateCustomer()

  const { mutate: getCustomerByEmail } = useGetCustomerByEmail()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    watch,
    getValues,
    formState: { errors },
  } = useForm<Inputs>({
    mode: 'all',
  })

  // True once the rep has touched the "Repeat customer" checkbox. The email
  // lookup below must never undo a manual choice.
  const repeatTouchedRef = useRef(false)

  const selectedBranchId = watch('branchId')

  const branchTimezone = branchesData?.find((branch) => branch._id === selectedBranchId)?.timezone || DEFAULT_TIMEZONE

  const email = watch('email')

  const [debouncedEmail] = useDebouncedValue(email, 1000)

  const wasOpenedRef = useRef(false)

  useEffect(() => {
    if (opened && !wasOpenedRef.current) {
      reset({
        firstName: '',
        lastName: '',
        phone: '',
        phoneType: 'Mobile',
        email: '',
        altFirstName: '',
        altLastName: '',
        altPhone: '',
        altPhoneType: 'Mobile',
        altEmail: '',
        branchId: currentMainBranch || branchId || '',
        requestedDay: getDefaultRequestedDay(),
        referredBy: '',
        previousContractId: '',
        isRepeat: false,
      })
      repeatTouchedRef.current = false
    }
    wasOpenedRef.current = opened
  }, [opened, reset, branchId, currentMainBranch, moveManagerDate])

  const onSubmit = async (data: any) => {
    const trimmedFirstName = data.firstName?.trim() ?? ''
    const trimmedLastName = data.lastName?.trim() ?? ''

    let hasValidationErrors = false

    if (!trimmedFirstName) {
      setError('firstName', { type: 'validate', message: 'First Name is required' })
      hasValidationErrors = true
    }

    if (!trimmedLastName) {
      setError('lastName', { type: 'validate', message: 'Last Name is required' })
      hasValidationErrors = true
    }

    if (hasValidationErrors) {
      return
    }

    const processedData = {
      ...data,
      firstName: trimmedFirstName,
      lastName: trimmedLastName,
      requestedDay: formatInTimeZone(data.requestedDay, branchTimezone, 'yyyy-MM-dd'),
    }

    try {
      await createCustomerAsync({ customer: processedData })
      onClose()
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (!debouncedEmail) return

    if (!emailRegex.test(debouncedEmail)) return

    getCustomerByEmail(
      { email: debouncedEmail.trim() },
      {
        onSuccess: (data) => {
          const found = data.exists !== ''
          if (found) {
            notifications.show({
              title: 'Customer Found',
              message: `A customer with this email already exists.`,
              color: 'yellow',
            })
            setValue('referredBy', REPEAT_REFERRAL, { shouldValidate: true })
            setValue('previousContractId', data.exists)
          } else {
            setValue('previousContractId', '')
          }
          // Only auto-toggle when the rep hasn't decided already: before this,
          // a no-match (or a failed lookup) reset a manually ticked checkbox,
          // which is why half of the "Repeat Customer" leads had isRepeat=false.
          const next = resolveRepeatAfterEmailLookup({
            found,
            repeatTouched: repeatTouchedRef.current,
            referredBy: getValues('referredBy'),
          })
          if (next !== undefined) setValue('isRepeat', next)
        },
        onError: () => {
          setValue('previousContractId', '')
        },
      },
    )
  }, [debouncedEmail])

  const isLoading = isLoadingMarketingSources || isLoadingBranches || isLoadingCreatingCustomer

  if (branchesError || marketingSourcesError)
    return (
      <Modal opened={opened} size="80%" onClose={onClose}>
        <Text fw="bolder" size="lg">
          Error loading data
        </Text>
      </Modal>
    )

  return (
    <Modal
      opened={opened}
      size="80%"
      title={
        <Text fw="bolder" size="lg">
          New Customer
        </Text>
      }
      onClose={onClose}
    >
      <LoadingOverlay visible={isLoading} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column" gap="20">
          <Text fw="bolder" size="lg">
            Primary Contact Information
          </Text>
          <Flex justify="space-between" gap="10">
            <Controller
              name="firstName"
              control={control}
              rules={{
                required: 'First Name is required',
                validate: (value) => {
                  if (!value || value.trim() === '') {
                    return 'First Name is required'
                  }
                  const trimmedValue = value.trim()
                  if (trimmedValue.length < 2 || trimmedValue.length > 20) {
                    return 'Name must be 2-20 characters.'
                  }
                  return true
                },
              }}
              render={({ field }) => (
                <TextInput
                  {...field}
                  required
                  value={field.value ? field.value : ''}
                  label="First Name"
                  placeholder="First Name"
                  flex={1}
                  error={errors.firstName?.message}
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              rules={{
                required: 'Last Name is required',
                validate: (value) => {
                  if (!value || value.trim() === '') {
                    return 'Last Name is required'
                  }
                  const trimmedValue = value.trim()
                  if (trimmedValue.length < 1 || trimmedValue.length > 20) {
                    return 'Name must be 1-20 characters.'
                  }
                  return true
                },
              }}
              render={({ field }) => (
                <TextInput
                  {...field}
                  required
                  value={field.value ? field.value : ''}
                  label="Last Name"
                  placeholder="Last Name"
                  flex={1}
                  error={errors.lastName?.message}
                />
              )}
            />
          </Flex>
          <Flex justify="space-between" gap="10">
            <Controller
              name="phone"
              control={control}
              rules={{
                required: 'Phone number is required',
                validate: (value) => {
                  const digitsOnly = value.replace(/\D/g, '')
                  return digitsOnly.length === 11 || 'Phone number must contain exactly 10 digits'
                },
              }}
              render={({ field }) => (
                <DSPhoneInput
                  {...field}
                  required
                  label="Phone Number"
                  placeholder="+1 (XXX) XXX-XXXX"
                  flex={1}
                  error={errors.phone?.message}
                />
              )}
            />
            <Controller
              name="phoneType"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  searchable
                  clearable
                  label="Phone Type"
                  flex={1}
                  placeholder="Phone Type"
                  data={phoneTypes}
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              rules={{
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: 'Invalid email format',
                },
                required: 'Email is required',
              }}
              render={({ field }) => (
                <TextInput
                  {...field}
                  required
                  value={field.value ? field.value : ''}
                  label="Email"
                  placeholder="Email"
                  flex={2}
                  error={errors.email?.message}
                />
              )}
            />
          </Flex>
          <Text fw="bolder" size="lg">
            Alternate Contact Information
          </Text>
          <Flex justify="space-between" gap="10">
            <Controller
              name="altFirstName"
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  value={field.value ? field.value : ''}
                  label="Alt. First Name"
                  placeholder="Alt. First Name"
                  flex={1}
                  error={errors.altFirstName?.message}
                />
              )}
            />
            <Controller
              name="altLastName"
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  value={field.value ? field.value : ''}
                  label="Alt. Last Name"
                  placeholder="Alt. Last Name"
                  flex={1}
                  error={errors.altLastName?.message}
                />
              )}
            />
          </Flex>
          <Flex justify="space-between" gap="10">
            <Controller
              name="altPhone"
              control={control}
              rules={{
                validate: (value) => {
                  if (!value || value.trim() === '') {
                    return true
                  }

                  const digitsOnly = value.replace(/\D/g, '')
                  return digitsOnly.length === 11 || 'Phone number must contain exactly 10 digits'
                },
              }}
              render={({ field }) => (
                <DSPhoneInput
                  {...field}
                  label="Alt. Phone Number"
                  placeholder="Alt. Phone Number"
                  flex={1}
                  error={errors.altPhone?.message}
                />
              )}
            />
            <Controller
              name="altPhoneType"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  searchable
                  clearable
                  label="Alt. Phone Type"
                  flex={1}
                  placeholder="Alt. Phone Type"
                  data={phoneTypes}
                />
              )}
            />
            <Controller
              name="altEmail"
              control={control}
              rules={{
                pattern: {
                  value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                  message: 'Invalid email format',
                },
              }}
              render={({ field }) => (
                <TextInput
                  {...field}
                  value={field.value ? field.value : ''}
                  label="Alt. Email"
                  placeholder="Alt. Email"
                  flex={2}
                  error={errors.altEmail?.message}
                />
              )}
            />
          </Flex>
          <Text fw="bolder" size="lg">
            Other Customer Details
          </Text>
          <Flex gap="10" flex={1}>
            <Controller
              name="branchId"
              control={control}
              rules={{
                required: 'Branch is required',
                validate: (value) => {
                  if (!value) return 'Branch is required'
                  const isValidBranch = branchesData?.some((branch) => branch._id === value)
                  return isValidBranch || 'Please select a valid branch'
                },
              }}
              render={({ field }) => (
                <Select
                  {...field}
                  searchable
                  required
                  value={field.value}
                  label="Branch"
                  flex={1}
                  error={errors.branchId?.message}
                  placeholder="Branch"
                  data={branchesData?.map((branch) => ({
                    label: branch.name,
                    value: branch._id,
                  }))}
                  nothingFoundMessage="No branches found"
                  onChange={(value) => {
                    if (!value || branchesData?.some((branch) => branch._id === value)) {
                      field.onChange(value)
                    }
                  }}
                  onSearchChange={() => {
                    if (errors.branchId) {
                      field.onChange(field.value)
                    }
                  }}
                />
              )}
            />
            <Controller
              name="requestedDay"
              control={control}
              rules={{
                required: 'Requested Day is required',
              }}
              render={({ field }) => (
                <DatePickerInput
                  {...field}
                  required
                  valueFormat="MM/DD/YYYY"
                  label="Requested Day"
                  placeholder="Requested Day"
                  flex={1}
                  value={field.value ? toZonedTime(field.value, branchTimezone) : new Date()}
                  error={errors.requestedDay?.message}
                  onChange={(e) => {
                    if (e) {
                      const zonedDate = fromZonedTime(parseLocalDate(e), branchTimezone)
                      field.onChange(zonedDate)
                    }
                  }}
                />
              )}
            />
          </Flex>
          <Flex gap="10" flex={1} justify="space-between" align="center">
            <Controller
              name="referredBy"
              control={control}
              rules={{
                required: 'Referral is required',
              }}
              render={({ field }) => (
                <Select
                  {...field}
                  searchable
                  required
                  clearable={false}
                  allowDeselect={false}
                  label="Referral"
                  placeholder="Referral"
                  flex={1}
                  error={errors.referredBy?.message}
                  value={marketingSourcesData?.find((source) => source.name === field.value)?.name}
                  data={marketingSourcesData?.map((source) => ({
                    label: source.name,
                    value: source.name,
                  }))}
                  nothingFoundMessage="No referral sources found"
                  onChange={(value) => {
                    field.onChange(value)
                    // Picking "Repeat Customer" as the referral is the rep saying
                    // it is a repeat — mirror it into the flag the API stores.
                    if (value === REPEAT_REFERRAL) setValue('isRepeat', true)
                  }}
                />
              )}
            />
            <Controller
              name="isRepeat"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  mt={15}
                  label="Repeat customer"
                  flex={1}
                  onChange={(event) => {
                    repeatTouchedRef.current = true
                    field.onChange(event.target.checked)
                  }}
                />
              )}
            />
          </Flex>
        </Flex>
        <Flex justify="flex-end" gap="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Add Customer</Button>
        </Flex>
      </form>
    </Modal>
  )
}

export default ModalNewCustomer
